// Les images TMDB/metahub à la bonne taille : fonctions pures, partagées par
// l'interface (src/main.js) et les versions (versions/coeur/images.js).
// Éprouvées par essais/images.mjs.

// Les paliers que chaque fournisseur sait servir, du plus petit au plus grand,
// avec la largeur reelle en pixels. Ce ne sont pas des noms inventes : ce sont
// les seules valeurs que ces hotes acceptent, et en demander une autre rend un
// 404 — donc un cadre vide.
//
// ⚠️ TMDB N'A PAS LA MEME ECHELLE POUR UNE AFFICHE ET POUR UN FOND. `w342`
// n'existe que pour les affiches, `w1280` que pour les fonds. C'est pourquoi
// l'appelant doit dire CE QU'IL DEMANDE : le deviner d'apres l'adresse est
// impossible, les deux ont la meme forme `/t/p/<taille>/<fichier>`.
const PALIERS_IMAGES = {
  'image.tmdb.org': {
    affiche: [['w92', 92], ['w154', 154], ['w185', 185], ['w342', 342], ['w500', 500], ['w780', 780], ['original', 2000]],
    fond: [['w300', 300], ['w780', 780], ['w1280', 1280], ['original', 3840]],
  },
  'images.metahub.space': {
    // ⚠️ ON NE REDUIT JAMAIS UNE AFFICHE METAHUB. `poster/medium` fait deja la
    // taille d'une carte, et `poster/small` (150 px) serait flou des qu'un ecran
    // a deux points par pixel. La liste ne porte donc que le palier courant et
    // celui du dessus : on peut monter, jamais descendre.
    affiche: [['medium', 300], ['large', 600]],
    fond: [['medium', 1280], ['large', 1920]],
  },
};

/** Une adresse servie par notre proxy, ouverte : `{ avant, url }` ou `null`. */
function ouvrirAdresseProxy(adresse) {
  // Le prefixe varie — `/api/img/…` en direct, `/.proxy/api/img/…` dans
  // l'iframe de Discord : on ne cherche donc que la fin.
  const marque = '/api/img/';
  const coupe = adresse.indexOf(marque);
  if (coupe < 0) return null;
  const enc = adresse.slice(coupe + marque.length);
  if (!enc || enc.includes('/')) return null;
  try {
    return { avant: adresse.slice(0, coupe + marque.length), url: decodeURIComponent(enc) };
  } catch {
    return null;
  }
}

/**
 * La meme image, dans le palier le plus proche de la place qu'elle occupe.
 *
 * `largeur` est une largeur en PIXELS D'ECRAN (densite comprise), pas en points
 * CSS : c'est le nombre de pixels que le navigateur va reellement peindre.
 *
 * ⚠️ ELLE EST IDEMPOTENTE, ET C'EST UNE PROPRIETE, PAS UN HASARD. La grille
 * repeint sa fenetre a chaque cran de molette ; si la reecriture rendait une
 * adresse differente au deuxieme passage, le navigateur retelechargerait la
 * meme image sous un autre nom — et l'on aurait fabrique le defaut qu'on
 * corrige. `imageDeTaille(imageDeTaille(u)) === imageDeTaille(u)`.
 *
 * ⚠️ ELLE NE TOUCHE PAS A L'HOTE. Elle ne remplace qu'un segment de chemin dans
 * une adresse deja validee par la liste blanche du serveur (`HOTES_IMAGES`) :
 * elle ne peut donc pas ouvrir une destination nouvelle. Une adresse d'un hote
 * qu'elle ne connait pas repart telle quelle.
 *
 * @param {string} adresse   ce que le serveur nous a donne, proxy compris
 * @param {number} largeur   la largeur peinte, en pixels d'ecran
 * @param {'affiche'|'fond'} genre  une affiche verticale, ou un fond large
 */
export function imageDeTaille(adresse, largeur, genre = 'affiche') {
  if (typeof adresse !== 'string' || !adresse) return adresse;
  const dedans = ouvrirAdresseProxy(adresse);
  if (dedans) {
    const change = imageDeTaille(dedans.url, largeur, genre);
    return change === dedans.url ? adresse : dedans.avant + encodeURIComponent(change);
  }

  let cible;
  try { cible = new URL(adresse); } catch { return adresse; }
  const paliers = PALIERS_IMAGES[cible.hostname]?.[genre];
  if (!paliers) return adresse;

  // Le palier retenu : le plus petit qui couvre encore la place. La tolerance
  // de 10 % evite de sauter au palier du dessus pour deux pixels — 344 pixels
  // demandes prennent `w342`, pas `w500` qui pese deux fois plus.
  const voulue = Math.max(1, Math.round(Number(largeur) || 0));
  const choisi = paliers.find(([, px]) => px >= voulue * 0.9) ?? paliers[paliers.length - 1];

  if (cible.hostname === 'image.tmdb.org') {
    const remplace = cible.pathname.replace(/^\/t\/p\/[^/]+\//, `/t/p/${choisi[0]}/`);
    if (remplace === cible.pathname) return adresse;
    cible.pathname = remplace;
    return cible.href;
  }
  // metahub : `/poster/medium/tt0903747/img` ou `/background/medium/tt…/img`.
  const attendu = genre === 'affiche' ? 'poster' : 'background';
  const m = new RegExp(`^/${attendu}/([^/]+)/`).exec(cible.pathname);
  if (!m) return adresse;
  // ⚠️ ON NE DESCEND PAS CHEZ METAHUB. Le palier courant peut valoir mieux que
  // celui qu'on calcule — metahub sert parfois du `large` de lui-meme — et
  // redescendre rendrait floue une image qui etait nette, pour rien : elle est
  // deja dans le cache du navigateur sous son adresse d'origine.
  const rangActuel = paliers.findIndex(([nom]) => nom === m[1]);
  const rangChoisi = paliers.indexOf(choisi);
  if (rangActuel >= 0 && rangChoisi <= rangActuel) return adresse;
  cible.pathname = cible.pathname.replace(`/${attendu}/${m[1]}/`, `/${attendu}/${choisi[0]}/`);
  return cible.href;
}

/**
 * Combien de pixels d'ecran une carte pose reellement.
 *
 * ⚠️ PLAFONNE A DEUX. Au-dela, l'oeil ne fait plus la difference sur une
 * vignette de sept centimetres, et l'image, elle, double de poids.
 */
export function densiteUtile(dpr = 1) {
  return Math.min(2, Math.max(1, Number(dpr) || 1));
}
